import React, { useEffect } from 'react';

import type { TableV2RecordType } from '@frontend/legacy-components';

import { getParentCheckboxState, selectChildren, selectRange } from './helpers';
import { useTableV2SelectionContext } from './useTableV2SelectionContext';

/**
 * A subhook that provides props to make checkboxes integrate with this system.
 * These checkboxes should be placed in a function given to the `render` prop
 * of a `column` object within the `columns` in the table; the `render` prop's function has access to `record`.
 * @param record the `record` of this checkboxes' row in TableV2.
 */
export const useTableV2Checkbox = (
  record: TableV2RecordType,
): {
  isChecked: boolean;
  isIndeterminate: boolean;
  isDisabled: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
} => {
  const {
    dataObj,
    selectedRows,
    updateSelectedRows,
    disabledOrOmittedRows,
    lastSelected,
    setLastSelected,
    shiftPressed,
  } = useTableV2SelectionContext();

  const { mixed, empty, full } = getParentCheckboxState(
    record,
    selectedRows,
    disabledOrOmittedRows,
  );

  useEffect(() => {
    // handle updating checked state of a parent depending on changes to
    // children
    if (record.children == null) return;
    else if (mixed && selectedRows?.includes(record.key)) {
      updateSelectedRows(selectedRows?.filter((el) => el != record.key));
    } else if (empty && selectedRows?.includes(record.key)) {
      updateSelectedRows(selectedRows?.filter((el) => el != record.key));
    } else if (full && !selectedRows?.includes(record.key)) {
      updateSelectedRows([...selectedRows, record.key]);
    }
  }, [empty, full, mixed, record, selectedRows, updateSelectedRows]);

  return {
    isChecked: selectedRows.includes(record.key),
    isIndeterminate: mixed,
    isDisabled: disabledOrOmittedRows.includes(record.key),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      // If we're pressing shift and have previously selected a checkbox,
      if (shiftPressed && lastSelected != null && lastSelected !== record.key) {
        // Perform selection between a range...
        // ... where this checkbox's child selection is handled by selectRange.
        selectRange(
          dataObj,
          selectedRows,
          updateSelectedRows,
          disabledOrOmittedRows,
          lastSelected,
          record,
          mixed,
        );
      }
      // If we're not pressing shift...
      // ...or are pressing shift but haven't selected anything before...
      // ...and we have children...
      else if (record.children != null) {
        // Recursively select the children.
        selectChildren(
          dataObj,
          record.key,
          mixed,
          selectedRows,
          updateSelectedRows,
          disabledOrOmittedRows,
        );
      } else if (record.children == null) {
        const nextSelectedRows = e.target.checked
          ? [...selectedRows, record.key]
          : selectedRows.filter(
              (currentSelectedRow) => currentSelectedRow !== record.key,
            );
        updateSelectedRows(nextSelectedRows);
      }
      // Set our last selected value after we've already dealt with any kind of shift selecting.
      setLastSelected(record.key);
    },
  };
};
